export const lastBrowseLocationKey = 'h24:last-browse-location'

interface AppLocation {
  pathname: string
  search?: string
  hash?: string
  state?: unknown
}

export interface ContextualNavigationState {
  returnTo: string
  returnState?: ContextualNavigationState
}

export interface ContextualReturn {
  to: string
  state?: ContextualNavigationState
  label: string
}

function safeReturnPath(value: unknown) {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null
  try {
    const url = new URL(value, 'https://h24.local')
    if (url.origin !== 'https://h24.local') return null
    const allowed = url.pathname === '/' || url.pathname === '/profilo' || url.pathname === '/admin' || url.pathname.startsWith('/citta/')
    return allowed ? `${url.pathname}${url.search}${url.hash}` : null
  } catch {
    return null
  }
}

function readNavigationState(value: unknown, depth = 0): ContextualNavigationState | undefined {
  if (depth > 4 || !value || typeof value !== 'object') return undefined
  const candidate = value as { returnTo?: unknown; returnState?: unknown }
  const returnTo = safeReturnPath(candidate.returnTo)
  if (!returnTo) return undefined
  const returnState = readNavigationState(candidate.returnState, depth + 1)
  return returnState ? { returnTo, returnState } : { returnTo }
}

export function appLocationPath(location: Pick<AppLocation, 'pathname' | 'search' | 'hash'>) {
  return `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`
}

export function createContextualNavigationState(location: AppLocation): ContextualNavigationState {
  const returnState = readNavigationState(location.state)
  const returnTo = safeReturnPath(appLocationPath(location)) ?? '/'
  return returnState ? { returnTo, returnState } : { returnTo }
}

export function rememberBrowseLocation(location: Pick<AppLocation, 'pathname' | 'search' | 'hash'>) {
  if (location.pathname !== '/' && !location.pathname.startsWith('/citta/')) return
  const path = safeReturnPath(appLocationPath(location))
  if (!path) return
  try {
    window.sessionStorage.setItem(lastBrowseLocationKey, path)
  } catch {
    // Navigation still works through router state when storage is unavailable.
  }
}

function readLastBrowseLocation() {
  try {
    return safeReturnPath(window.sessionStorage.getItem(lastBrowseLocationKey))
  } catch {
    return null
  }
}

function returnLabel(path: string) {
  const pathname = new URL(path, 'https://h24.local').pathname
  if (pathname.startsWith('/citta/')) return 'Torna alla mappa'
  if (pathname === '/profilo') return 'Torna al profilo'
  if (pathname === '/admin') return 'Torna all’amministrazione'
  return 'Torna alla mappa'
}

export function resolveContextualReturn(locationState: unknown): ContextualReturn {
  const explicit = readNavigationState(locationState)
  const to = explicit?.returnTo ?? readLastBrowseLocation() ?? '/'
  return {
    to,
    state: explicit?.returnState,
    label: returnLabel(to),
  }
}
